import { useRef, useState } from "react";
import { DoRenameExistingOpenDoc, UpdateChartMetadata, UpdatePlans } from "@/Utils/Promises";
import { isNullish } from "@theorgchartdev/storybook";
import {
  ValidateRename,
  GetNameValidation,
  enumNameValidation
} from "@coreapp/utils/ValidateRename";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useReportingContext } from "@/providers/ReportsProvider";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { appstr } from "@coreapp/utils/appstr";
import { IsOrgChartFile } from "@coreapp/utils/IsOrgChartFile";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumChartOpenModes, enumUIModes, kAdminUser, kRWUser } from "@coreapp/constants";
import { GetExtension } from "@coreapp/utils/GetExtension";
import { isInReporting } from "@coreapp/utils/isInReporting";

export const useRenameDocController = () => {
  const nav = useAppNavigate();
  const wsMgr = GetWSMgr();
  const doc = GetDoc();
  const planMgr = wsMgr?.GetPlanMgr();
  const docMgr = wsMgr?.GetDocMgr();
  const iWiz = docMgr?.GetIWizDataRefresh();
  const openCtrlRef = useRef(null);
  const nameInputRef = useRef(null);
  const descInputRef = useRef(null);
  const popoverTitle = docMgr ? docMgr.GetFormattedChartName() : appstr("Chart information");
  const [inEditMode, setInEditMode] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const { currentOpenReport, reports, UpdateOpenReport } = useReportingContext();
  const { canAccessChartExplorer } = usePermissionCheck();

  const GetCurrentDescription = () => {
    return doc?.IsPlanChart() ? planMgr?.GetPlanDescription() : iWiz?.GetDescription();
  };

  const GetCurrentName = () => {
    const doc = GetDoc();
    const planMgr = wsMgr?.GetPlanMgr();
    const docMgr = wsMgr?.GetDocMgr();
    return doc?.IsPlanChart() ? planMgr?.GetPlanName() : docMgr?.GetFileName();
  };

  const GetDisplayName = () => {
    let name = GetCurrentName();
    if (!name) name = appstr("Untitled");
    return name;
  };

  const toggleEditMode = () => {
    if (!canEdit()) return;
    if (inEditMode) {
      HandleSave();
    } else {
      setInEditMode(!inEditMode);
      if (!Globals._uiConfig.InWorkbenchEditMode() && IsOrgChartFile(docMgr.GetFileName(true))) {
        nav.setMode("edit");
      }
    }
  };

  const HandleSave = () => {
    SaveSettings().then(saved => {
      if (!saved) return;
      const keepOpen = !saved;
      setInEditMode(keepOpen);
    });
  };

  const GetInputDescription = () => {
    return descInputRef?.current?.value;
  };

  const GetInputName = () => {
    return nameInputRef?.current?.value ?? "";
  };

  const SavePlanDetails = async ({ input, desc, objective }) => {
    const planMgr = wsMgr.fPlanManager;
    const plan = planMgr.fPlanInfo;
    let data = { ID: parseInt(plan.ID), name: input };
    if (!isNullish(desc)) {
      data.description = desc;
    }
    if (!isNullish(objective)) {
      data.objective = objective;
    }

    const { success } = await UpdatePlans(data, "saveplan");
    if (success) {
      planMgr.SetPlanName(input);
      if (!isNullish(desc)) planMgr.SetPlanDescription(desc);
      if (!isNullish(objective)) planMgr.SetPlanObjective(objective);
      planMgr.UpdateContext();
      docMgr.SetFileName(input);
      wsMgr.SignalDocChanged();
    }
  };

  const SaveBaseChartDescription = async ({ input, desc }) => {
    let data = { name: input };
    if (!isNullish(desc)) {
      data.description = desc;
    }
    const { success } = await UpdateChartMetadata(doc?.GetChartID(), JSON.stringify(data));
    if (success) {
      const updatedChartsList = wsMgr.fOrgInfo.GetMasterChartList().map(aChart => {
        return {
          ...aChart,
          name: aChart["ID"] === doc.GetChartID() ? input : aChart["name"]
        };
      });
      if (!isNullish(desc)) iWiz.SetDescription(desc);
      docMgr.SetFileName(input);
      wsMgr.fOrgInfo.SetMasterChartList(updatedChartsList);
      wsMgr.SignalDocChanged();
    }
  };

  const DoRenameSave = async ({ input }) => {
    const openDoc = docMgr.GetFileName(true);
    const dirLocked = docMgr.GetDirLocked();
    const directory = docMgr.GetFileDirectory();
    const ext = GetExtension(openDoc);
    let newFullFilename = ext.length > 0 ? input + "." + ext : input;
    const { response } = await DoRenameExistingOpenDoc(newFullFilename);
    if (response["success"]) {
      let newName = response["message"];
      docMgr.SetFileName(newName);
      docMgr.SetFileDirectory(directory, dirLocked);
      Globals._uiConfig.SetDocConfig(directory, newName);
      Globals._uiConfig.SetChartOpenMode(enumChartOpenModes.opendoc);
      wsMgr.SignalDocChanged();
    }
  };

  const SaveCustomChartDescription = async ({ input, desc }) => {
    const openDoc = docMgr.GetFileName(true);
    const openDir = docMgr.GetFileDirectory();
    const curName = docMgr.GetFileName();
    const curDesc = iWiz.GetDescription() ?? "";
    const isExistingDoc = !isNullish(openDoc) && !isNullish(openDir) && IsOrgChartFile(openDoc);
    if (isExistingDoc) {
      if (curName !== input) {
        await DoRenameSave({ input });
      }
    } else {
      docMgr.SetFileName(input + ".ocd");
    }
    if (!isNullish(desc) && curDesc !== desc) {
      iWiz.SetDescription(desc);
      wsMgr.SetDirty();
    }
    wsMgr.SignalDocChanged();
  };

  const isUniqueReportName = name => {
    return !reports.some(r => {
      const nameMatches = r.name === name;
      const idMatches = r.ID === currentOpenReport.ID;
      return nameMatches && !idMatches;
    });
  };

  const SaveSettings = async externalDetails => {
    var serverChart = doc?.IsServerChart();

    let details = {};
    if (externalDetails) {
      details = externalDetails;
    } else {
      if (!isNullish(nameInputRef?.current)) {
        details.input = GetInputName();
      }
      if (!isNullish(descInputRef?.current)) {
        details.desc = GetInputDescription();
      }
    }

    if (!serverChart) {
      const nameToValidate = details.input ?? "";
      const { valid, message } = ValidateRename(nameToValidate, wsMgr);
      if (!valid) {
        setErrorMsg(message);
        return false;
      }
      setErrorMsg("");
    }

    if (isInReporting()) {
      await SaveReportSettings();
    } else if (doc?.IsPlanChart()) {
      await SavePlanDetails(details);
    } else if (serverChart) {
      await SaveBaseChartDescription(details);
    } else {
      await SaveCustomChartDescription(details);
    }
    ReRenderCanvas();
    return true;
  };

  const SaveReportSettings = async () => {
    const curName = GetInputName();
    const reason = GetNameValidation(curName);
    if (reason !== enumNameValidation.valid) {
      //setNameErrorMsg(GetNameValidationMessage(reason));
      return false;
    }

    if (!isUniqueReportName(curName)) {
      //setNameErrorMsg(appstr("nameinuse"));
      return false;
    }

    //const curDesc = GetInputDescription();
    UpdateOpenReport({ name: curName });
    return true;
  };

  // edit permission check
  const canEdit = () => {
    if (!doc) return false;
    // can only edit in these modes
    if (
      ![enumUIModes.planning, enumUIModes.chart, enumUIModes.edit, enumUIModes.reports].includes(
        Globals._uiConfig.GetUIMode()
      )
    )
      return false;
    const userRole = Globals._accountInfo.GetUserRole();
    if (doc?.IsPlanChart()) {
      const { canRename } = getPlanPermissions({ useCurrentPlan: true });
      return canRename;
      // only admins can rename base charts
    } else if (doc?.IsServerChart() && userRole !== kAdminUser) {
      return false;
    }
    // only admins and rw users can rename custom charts
    else if (![kAdminUser, kRWUser].includes(userRole)) {
      return false;
    }
    return canAccessChartExplorer;
  };

  return {
    canEdit,
    DoRenameSave,
    setInEditMode,
    inEditMode,
    errorMsg,
    setErrorMsg,
    SaveSettings,
    SaveCustomChartDescription,
    toggleEditMode,
    HandleSave,
    GetInputName,
    GetInputDescription,
    SaveBaseChartDescription,
    SavePlanDetails,
    nameInputRef,
    descInputRef,
    popoverTitle,
    openCtrlRef,
    GetCurrentName,
    GetDisplayName,
    GetCurrentDescription
  };
};
